/**
 * property-open-elsewhere.tsx
 *
 * Whether the tab shows a property that is no longer the one open.
 *
 * The open property is server-side state every tab, window and device of a
 * user shares, and a page reads it once. The shell finds out when another
 * property has been opened since the tab loaded (`useOpenPropertyWatch`) and
 * shows `PropertyOpenedElsewhereNotice` in place of the page. Two things beside
 * the page need to know as well, without a flag threaded through the rail and
 * the page router: the rail, whose sync indicator and "Ask Optivo" status read
 * the open property and would state the other one's under this one's name; and
 * the strategy page, which keeps an unsaved form hidden under the notice and
 * must not go on reading the open property beside it.
 *
 * Outside a provider the answer is `false`, so a component rendered on its own
 * — in a test, in the wizard — behaves as it did before the shell watched.
 */

import { createContext, useContext, type ReactNode } from "react";

const PropertyOpenElsewhereContext = createContext(false);

export function PropertyOpenElsewhereProvider({ openElsewhere, children }: { openElsewhere: boolean; children: ReactNode }) {
    return <PropertyOpenElsewhereContext.Provider value={openElsewhere}>{children}</PropertyOpenElsewhereContext.Provider>;
}

/** True while another property is open than the one the tab was loaded for. */
export function usePropertyOpenElsewhere(): boolean {
    return useContext(PropertyOpenElsewhereContext);
}
